import {
  Timestamp,
  addDoc,
  collection,
  collectionGroup,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';

import { firestore } from './FirebaseService';

export const INCIDENT_TEXT_MAX_LENGTH = 500;

const usersCollectionName = 'users';
const incidentsCollectionName = 'incidents';

const incidentsRef = (patientId) =>
  collection(firestore, usersCollectionName, patientId, incidentsCollectionName);

const mapIncidentDoc = (docSnap) => {
  const data = docSnap.data();
  return {
    id: docSnap.id,
    text: data?.text ?? '',
    createdBy: data?.createdBy ?? null,
    createdAt: data?.createdAt ?? null,
    updatedAt: data?.updatedAt ?? null,
    status: data?.status ?? 'open',
    resolvedAt: data?.resolvedAt ?? null,
    resolvedBy: data?.resolvedBy ?? null,
  };
};

export const getPatientIncidents = async (patientId) => {
  if (!patientId) return [];
  const q = query(incidentsRef(patientId), orderBy('createdAt', 'desc'));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(mapIncidentDoc);
};

export const createPatientIncident = async (patientId, { text, createdAt, createdBy }) => {
  if (!patientId) throw new Error('Paciente no válido');
  const trimmed = text?.trim() ?? '';
  if (!trimmed) throw new Error('El texto es obligatorio');
  if (trimmed.length > INCIDENT_TEXT_MAX_LENGTH) {
    throw new Error(`El texto no puede superar ${INCIDENT_TEXT_MAX_LENGTH} caracteres`);
  }
  if (!createdAt) throw new Error('La fecha es obligatoria');
  if (!createdBy) throw new Error('Usuario no autenticado');

  const createdAtTimestamp =
    createdAt instanceof Timestamp ? createdAt : Timestamp.fromDate(createdAt);

  await addDoc(incidentsRef(patientId), {
    text: trimmed,
    createdBy,
    createdAt: createdAtTimestamp,
    updatedAt: serverTimestamp(),
    status: 'open',
  });
};

export const updatePatientIncident = async (patientId, incidentId, { text, createdAt }) => {
  if (!patientId || !incidentId) throw new Error('Incidencia no válida');
  const trimmed = text?.trim() ?? '';
  if (!trimmed) throw new Error('El texto es obligatorio');
  if (trimmed.length > INCIDENT_TEXT_MAX_LENGTH) {
    throw new Error(`El texto no puede superar ${INCIDENT_TEXT_MAX_LENGTH} caracteres`);
  }
  if (!createdAt) throw new Error('La fecha es obligatoria');

  const createdAtTimestamp =
    createdAt instanceof Timestamp ? createdAt : Timestamp.fromDate(createdAt);

  const docRef = doc(
    firestore,
    usersCollectionName,
    patientId,
    incidentsCollectionName,
    incidentId
  );
  await updateDoc(docRef, {
    text: trimmed,
    createdAt: createdAtTimestamp,
    updatedAt: serverTimestamp(),
  });
};

export const getAllIncidents = async () => {
  const q = query(collectionGroup(firestore, incidentsCollectionName), orderBy('createdAt', 'desc'));
  const snapshot = await getDocs(q);
  return snapshot.docs.map((docSnap) => ({
    ...mapIncidentDoc(docSnap),
    patientId: docSnap.ref.parent.parent.id,
  }));
};

export const resolveIncident = async (patientId, incidentId, resolvedBy) => {
  if (!patientId || !incidentId) throw new Error('Incidencia no válida');
  const docRef = doc(firestore, usersCollectionName, patientId, incidentsCollectionName, incidentId);
  await updateDoc(docRef, {
    status: 'resolved',
    resolvedAt: serverTimestamp(),
    resolvedBy,
    updatedAt: serverTimestamp(),
  });
};

export const deletePatientIncident = async (patientId, incidentId) => {
  if (!patientId || !incidentId) throw new Error('Incidencia no válida');
  const docRef = doc(
    firestore,
    usersCollectionName,
    patientId,
    incidentsCollectionName,
    incidentId
  );
  await deleteDoc(docRef);
};
